import * as _ from "lodash";

import { BatchUpdateElements } from "../actions/AddOrUpdateElement";
import { CopiedElement } from "../actions/PasteElements";
import { Triggers } from "../model/ITriggerData";

import { HashMap } from "../../shared/model/HashMap";
import { BpmnJsCliWrapper } from "./BpmnJsCliWrapper";
import { ElementType, IConditionBranchElement, IElement, IElementDictionary, IElementWithExpression, SharedStateMethods } from "./IProcessData";
import { getIntermediateCatchEventEventDefinitionType } from "processEditor/ElementTypeHelpers";
import { NativeAiParameter } from "./NativeAiParameter";

export const USE_STATEMENT_MODE = "useStatementMode";
export const USE_DMN_MODE = "useDmnMode";
export const USE_NATIVE_AI_MODE = "useNativeAiMode";
export const USE_VDM_MODE = "useVdmMode";
export const USE_SIGNAL_REHYDRATION_MODE = "useSignalRehydrationMode";

const shapeModesByElementType: ReadonlyMap<ElementType, string> = new Map([
    [ElementType.Vdm, USE_VDM_MODE],
    [ElementType.Dmn, USE_DMN_MODE],
    [ElementType.NativeAi, USE_NATIVE_AI_MODE],
    [ElementType.SignalRehydration, USE_SIGNAL_REHYDRATION_MODE]
]);

/**
 * Several element types share a BPMN element type and are only told apart by one of the mode flags above.
 * Resolves the flag of the given element, or undefined for elements the BPMN model describes on its own.
 * Statement mode is read from the parameters because it has no element type of its own.
 */
export function getShapeMode(element: IElement): string | undefined {
    if (element.type === ElementType.Expression) {
        return element.parameters?.[USE_STATEMENT_MODE]?.value ? USE_STATEMENT_MODE : undefined;
    }

    return shapeModesByElementType.get(element.type);
}

export function getElementType(type: string): ElementType | undefined {
    switch (type) {
        case "bpmn:Task": return ElementType.Task;
        case "bpmn:SequenceFlow": return ElementType.SequenceFlow;
        case "bpmn:ExclusiveGateway": return ElementType.Decision;
        case "bpmn:StartEvent": return ElementType.Start;
        case "bpmn:EndEvent": return ElementType.Return;
        case "bpmn:IntermediateThrowEvent": return ElementType.Throw;
        case "bpmn:CallActivity": return ElementType.CallActivity;
        case "bpmn:SubProcess": return ElementType.SubProcess;
        case "bpmn:IntermediateCatchEvent": return ElementType.Catch;
        case "bpmn:ScriptTask": return ElementType.Expression;
        case "bpmn:InclusiveGateway": return ElementType.InclusiveGateway;
        case "bpmn:BusinessRuleTask": return ElementType.SharedState;
        case "bpmn:DataObjectReference": return ElementType.DataObjectReference;
        case "bpmn:DataStoreReference": return ElementType.DataStoreReference;
    }
}

export function createElementEntry(bpmnElement: BpmnJsElement, elementParameters: IElementDictionary, eventType: string | null): IElement | undefined {
    const parsedType = getElementType(bpmnElement.type);
    const existingParameters: IElement = elementParameters[bpmnElement.id] || {} as IElement;

    switch (parsedType) {
        case ElementType.Start:
            return getStartNode(bpmnElement, existingParameters, elementParameters);
        case ElementType.Vdm:
        case ElementType.Dmn:
        case ElementType.SharedState:
            return createBusinessRuleTaskElement(bpmnElement, existingParameters, parsedType);
        case ElementType.CallActivity:
        case ElementType.Task:
        case ElementType.NativeAi: {
            const isNativeAiTask = (bpmnElement as any)?.[USE_NATIVE_AI_MODE]|| existingParameters?.parameters?.[USE_NATIVE_AI_MODE]?.value;
            if (isNativeAiTask) {
                return {
                    id: bpmnElement.id,
                    name: bpmnElement.businessObject.name,
                    type: ElementType.NativeAi,
                    selectedTypeId: "NativeAi",
                    parameters: existingParameters.parameters || {
                        [USE_NATIVE_AI_MODE]: {
                            mode: "toggle",
                            value: true
                        },
                        [NativeAiParameter.UseCustomOptions]: "true",
                        [NativeAiParameter.OptionsJson]: "{\n\t\n}",
                        [NativeAiParameter.IncludeReasoningSteps]: "false"
                    },
                }
            }

            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: parsedType,
                selectedTypeId: existingParameters.selectedTypeId,
                parameters: existingParameters.parameters || {},
                shouldRetry: bpmnElement.businessObject.loopCharacteristics ? true : false
            };
        }
        case ElementType.Return:
            let realReturnType: ElementType;
            if (bpmnElement.businessObject.eventDefinitions &&
                bpmnElement.businessObject.eventDefinitions.find((e) => e.$type === "bpmn:SignalEventDefinition")) {
                realReturnType = ElementType.IntermediateReturn;
            } else {
                realReturnType = parsedType;
            }
            return {
                id: bpmnElement.id,
                type: realReturnType,
                parameters: existingParameters.parameters || {
                    expression: { mode: "csharp", value: "#result" }
                }
            } as IElementWithExpression;
        case ElementType.Throw:
            return {
                id: bpmnElement.id,
                type: parsedType,
                parameters: existingParameters.parameters || {
                    expression: { mode: "csharp", value: "\"An exception has occurred\"" },
                    bypassGlobalExceptionHandler: { mode: "toggle", value: true } // Probably the more sensible default
                }
            };
        case ElementType.Decision:
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: parsedType,
                parameters: existingParameters.parameters || { expression: { mode: "csharp", value: "true" } }
            };
        case ElementType.InclusiveGateway: {
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: ElementType.InclusiveGateway,
                parameters: {}
            };
        }

        case ElementType.SequenceFlow:
            const connElem: BpmnJsConnectionElement = bpmnElement as BpmnJsConnectionElement;
            const sourceElem = connElem.source;
            const ret = {
                id: connElem.id,
                type: parsedType,
            } as IElement;

            if (sourceElem) {
                const sourceType = getElementType(sourceElem.type);
                // If sequenceflow is part of the decicion then we need add decion data to it
                if ((sourceType === ElementType.Decision)) {
                    ret.name = connElem.businessObject.name;
                    ret.type = ElementType.ConditionBranch;
                    ret.isDefault = connElem.source?.businessObject.default ? connElem.source.businessObject.default.id === connElem.id : false;
                    ret.parameters = existingParameters.parameters || {};

                    if (!ret.name) { // only for new connections
                        if (sourceElem.outgoing !== undefined && sourceElem.outgoing.length <= 1 || sourceElem.businessObject.default) {
                            // the first non-default connection is being drawn -> set name to "yes"
                            setDecisionConnectionName(ret, "yes");
                        } else {
                            setDecisionConnectionName(ret, "no");
                            // If the source does not have a default connection, make this connection the default one:
                            if (!sourceElem.businessObject.default) {
                                BpmnJsCliWrapper.toggleDefaultBranch(ret.id);
                                ret.isDefault = true;
                            }
                        }
                    }
                    return ret as IConditionBranchElement;
                } else if ((sourceType === ElementType.InclusiveGateway)) {
                    ret.name = connElem.businessObject.name;
                    ret.type = ElementType.InclusiveDecisionBranch;
                    ret.isDefault = connElem.source?.businessObject.default ? connElem.source.businessObject.default.id === connElem.id : false;
                    ret.parameters = existingParameters.parameters || {
                        expression: { mode: "csharp", value: "" }
                    };
                    return ret as IConditionBranchElement;
                }
            }
            return ret;
        case ElementType.SubProcess:
            return createSubProcessEntry(bpmnElement, existingParameters, eventType);
        case ElementType.Catch:
            return createIntermediateCatchEventEntry(bpmnElement, existingParameters);
        case ElementType.Expression:
            if ((bpmnElement as any)[USE_STATEMENT_MODE]) {
                return {
                    id: bpmnElement.id,
                    name: bpmnElement.businessObject.name,
                    type: parsedType,
                    parameters: existingParameters.parameters || {
                        [USE_STATEMENT_MODE]: {
                            mode: "toggle",
                            value: true
                        },
                        variableExpression: {
                            mode: "csharp",
                            value: "{\n\n}"
                        },
                        shouldAssignVariable: {
                            mode: "toggle",
                            value: false
                        }
                    }
                };
            }
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: parsedType,
                parameters: existingParameters.parameters || {
                    variableName: "variable",
                    variableExpression: {
                        mode: "csharp",
                        value: "false"
                    },
                    shouldAssignVariable: {
                        mode: "toggle",
                        value: true
                    }
                }
            };

        case ElementType.DataObjectReference:
        case ElementType.DataStoreReference:
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: parsedType,
                parameters: {}
            };

        default:
            break; // do not return anything for elements we do not support
    }
}

function createBusinessRuleTaskElement(bpmnElement: BpmnJsElement, existingParameters: IElement, parsedType: ElementType): IElement {
    const isVdmTask = (bpmnElement as any)?.[USE_VDM_MODE] || existingParameters?.parameters?.[USE_VDM_MODE]?.value;
    if (isVdmTask) {
        return {
            id: bpmnElement.id,
            name: bpmnElement.businessObject.name,
            type: ElementType.Vdm,
            parameters: existingParameters.parameters || {
                [USE_VDM_MODE]: {
                    mode: "toggle",
                    value: true
                },
            }
        };
    }

    const isDmnTask = (bpmnElement as any)?.[USE_DMN_MODE] || existingParameters?.parameters?.[USE_DMN_MODE]?.value;
    if (isDmnTask) {
        return {
            id: bpmnElement.id,
            name: bpmnElement.businessObject.name,
            type: ElementType.Dmn,
            selectedTypeId: "Dmn",
            parameters: existingParameters.parameters || {
                [USE_DMN_MODE]: {
                    mode: "toggle",
                    value: true
                },
            }
        };
    }

    return {
        id: bpmnElement.id,
        name: bpmnElement.businessObject.name,
        type: parsedType,
        selectedTypeId: existingParameters.selectedTypeId || SharedStateMethods.AddOrUpdate,
        parameters: existingParameters.parameters || {
            keyExpression: { mode: "text", value: "key" },
            valueExpression: { mode: "csharp", value: "" },
            ttlValue: { mode: "integer", value: 60 },
            ttlMultiplier: 60, // Minutes
            throwIfFalse: true,
            isGlobalScope: false
        }
    };
}

function setDecisionConnectionName(elem: IElement, name: string) {
    BpmnJsCliWrapper.setLabel(elem.id, name);
    elem.name = name;
}

export function getCopiedElementParameters(createdElements: BpmnJsElement[], createdElementSourceMap: HashMap<string>, clipboardParameters: IElementDictionary): CopiedElement[] {
    const copiedParameters = [] as CopiedElement[];
    for (const element of createdElements) {
        const sourceId = createdElementSourceMap[element.id];
        if (clipboardParameters && clipboardParameters[sourceId]) {
            const newElement = _.cloneDeep(clipboardParameters[sourceId]) as IElement;
            newElement.id = element.id;
            copiedParameters.push({ id: element.id, element: newElement } as CopiedElement);
        }
    }
    return copiedParameters;
}

export function createBatchUpdate(elements: BpmnJsElement[], elementParameters: IElementDictionary): BatchUpdateElements {
    const ret = { added: [], updated: [], deleted: [] } as BatchUpdateElements;
    for (const element of elements) {
        if (element.parent == null && elementParameters[element.id]) {
            ret.deleted.push(elementParameters[element.id]);
        } else if (element.parent !== null && element.parent !== undefined) {
            const entry = createElementEntry(element, elementParameters, null);
            if (entry) {
                if (entry.type === ElementType.SequenceFlow) {
                    // We don't want to track sequence flows in our element parameters
                    if (elementParameters[entry.id]) {
                        ret.deleted.push(entry);
                    }

                } else if (elementParameters[element.id]) {
                    ret.updated.push(entry);
                } else {
                    ret.added.push(entry);
                }
            }
        }
    }
    return ret;
}

export function getStartNode(bpmnElement: BpmnJsElement, existingParameters: IElement, elementParameters: IElementDictionary): IElement {
    const parent = bpmnElement.parent;
    const parentType = parent ? getElementType(parent.type) : undefined;

    if (parentType === ElementType.SubProcess) {
        return {
            selectedTypeId: "",
            id: bpmnElement.id,
            name: bpmnElement.businessObject.name,
            type: ElementType.SubProcessStartNode,
            parameters: {},
        };
    }
    return {
        selectedTypeId: existingParameters.selectedTypeId || Triggers.ManualTrigger.triggerType,
        id: bpmnElement.id,
        name: bpmnElement.businessObject.name,
        type: ElementType.Start,
        parameters: existingParameters.parameters || {},
    };
}

export function createSubProcessEntry(bpmnElement: BpmnJsElement, existingParameters: IElement, eventType: string | null): IElement | undefined {
    const businessElement = bpmnElement.businessObject as any;
    if (businessElement) {
        const loopCharacteristics = businessElement.loopCharacteristics;
        if (loopCharacteristics && loopCharacteristics.$type === "bpmn:MultiInstanceLoopCharacteristics" && loopCharacteristics.isSequential) {
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: ElementType.SequentialForeach,
                parameters: existingParameters.parameters || {
                    variable: "i",
                    expression: { mode: "csharp", value: "new int[]{1,2,3}" }
                }
            };
        } else if (loopCharacteristics && loopCharacteristics.$type === "bpmn:MultiInstanceLoopCharacteristics" && !loopCharacteristics.isSequential) {
            // THIS DOES NOT EXISTS. It is for parallel foreach
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: ElementType.ParallelForeach,
                parameters: existingParameters.parameters || {
                    variable: "i",
                    expression: { mode: "csharp", value: "new int[]{1,2,3}" }
                }
            };
        } else if (loopCharacteristics && loopCharacteristics.$type === "bpmn:StandardLoopCharacteristics") {
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: ElementType.While,
                parameters: existingParameters.parameters || {
                    maxIterations: { mode: "integer", value: "1000" },
                    expression: { mode: "csharp", value: "true" }
                }
            };
        } else if (!loopCharacteristics) {
            return {
                id: bpmnElement.id,
                name: bpmnElement.businessObject.name,
                type: ElementType.SubProcess,
                parameters: existingParameters.parameters || {}
            };
        }
    }
}

function createIntermediateCatchEventEntry(bpmnElement: BpmnJsElement, existingParameters: IElement): IElement {
    
    const derivedType = getIntermediateCatchEventEventDefinitionType(bpmnElement, existingParameters);
    let parameters = existingParameters.parameters;
    
    if (!parameters) {

        if (derivedType == ElementType.Catch) {
            parameters = { expression: "error" };
        }

        else if (derivedType == ElementType.HydrationPoint) {
            parameters = { 
                correlationId: { mode: "csharp", value: "#process.executionid" }, 
                ttlMinutes: { mode: "integer", value: 1440 }, 
                continueExecution: { mode: "toggle", value: false } 
            };
        }

        else if (derivedType == ElementType.ScheduledRehydration) {
            parameters = { 
                maxIterations: { mode: "integer", value: 1 }, 
                rehydrationIntervalMinutes: { mode: "integer", value: 60 }
            };
        }

        else if (derivedType == ElementType.SignalRehydration) {
            parameters = { 
                [USE_SIGNAL_REHYDRATION_MODE]: { mode: "toggle", value: true }, // Used when drawing custom shape
                correlationId: { mode: "csharp", value: "" }
            };
        }
    }

    return {
        id: bpmnElement.id,
        name: bpmnElement.businessObject.name,
        type: derivedType!,
        parameters: parameters
    };
}
